import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Form, Table, Spinner, Alert, Badge } from 'react-bootstrap';
import api from '../api/axiosConfig';
import { BillingState, Invoice, InvoiceItem, UpdateInvoicePricesPayload } from '../types';
import { groupByDoctor } from '../utils/groupByDoctor';

interface Props {
    invoiceId: number;
    show: boolean;
    onClose: () => void;
    onSaved: () => void;
}

const stateLabels: Record<BillingState, string> = {
    STARTED: 'Начат',
    ANALYZED: 'Проанализирован',
    DIAGNOSED: 'Диагноз получен',
    REPORTED: 'Отчёт сформирован',
    FAILED: 'Ошибка',
};

const stateVariants: Record<BillingState, string> = {
    STARTED: 'secondary',
    ANALYZED: 'info',
    DIAGNOSED: 'primary',
    REPORTED: 'success',
    FAILED: 'danger',
};

const EditInvoicePricesModal: React.FC<Props> = ({ invoiceId, show, onClose, onSaved }) => {
    const [invoice, setInvoice] = useState<Invoice | null>(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [prices, setPrices] = useState<Record<number, string>>({});
    const [defaultPrice, setDefaultPrice] = useState('');

    useEffect(() => {
        if (!show) return;
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const res = await api.get<Invoice>(`/invoices/${invoiceId}`);
                setInvoice(res.data);
                const initial: Record<number, string> = {};
                for (const it of res.data.items) {
                    initial[it.id] = it.unitPrice != null ? String(it.unitPrice) : '';
                }
                setPrices(initial);
            } catch (e: any) {
                setError(e.response?.data?.message || 'Не удалось загрузить счёт');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [show, invoiceId]);

    const applyDefault = () => {
        if (!defaultPrice || !invoice) return;
        const next = { ...prices };
        for (const it of invoice.items) {
            if (!next[it.id]) next[it.id] = defaultPrice;
        }
        setPrices(next);
    };

    const total = useMemo(() => {
        let s = 0;
        for (const id of Object.keys(prices)) {
            const v = parseFloat(prices[Number(id)] || '');
            if (!isNaN(v)) s += v;
        }
        return s;
    }, [prices]);

    const groups = useMemo(() => invoice ? groupByDoctor(invoice.items) : [], [invoice]);

    const subtotalFor = (items: InvoiceItem[]) => {
        let s = 0;
        for (const it of items) {
            const v = parseFloat(prices[it.id] || '');
            if (!isNaN(v)) s += v;
        }
        return s;
    };

    const save = async () => {
        if (!invoice) return;
        setError(null);
        const payload: UpdateInvoicePricesPayload = { prices: [] };
        for (const it of invoice.items) {
            const raw = prices[it.id];
            if (raw === '' || raw === undefined) {
                payload.prices.push({ itemId: it.id, unitPrice: null });
                continue;
            }
            const v = parseFloat(raw);
            if (isNaN(v) || v < 0) {
                setError(`Некорректная цена для позиции №${it.id}`);
                return;
            }
            payload.prices.push({ itemId: it.id, unitPrice: v });
        }
        setSaving(true);
        try {
            await api.patch(`/invoices/${invoiceId}/prices`, payload);
            onSaved();
        } catch (e: any) {
            setError(e.response?.data?.message || 'Не удалось сохранить цены');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal show={show} onHide={onClose} size="xl" backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>Цены в счёте {invoice?.invoiceNumber ?? ''}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && <Alert variant="danger">{error}</Alert>}
                {loading ? (
                    <div className="d-flex justify-content-center py-5"><Spinner animation="border" /></div>
                ) : invoice ? (
                    <>
                        <div className="d-flex gap-2 mb-3 align-items-end">
                            <div>
                                <Form.Label className="small text-muted mb-1">Цена по умолчанию</Form.Label>
                                <Form.Control type="number" min="0" step="100" style={{ width: 200 }}
                                              value={defaultPrice}
                                              onChange={(e) => setDefaultPrice(e.target.value)} />
                            </div>
                            <Button variant="outline-secondary" onClick={applyDefault}>
                                Применить к пустым
                            </Button>
                            <div className="ms-auto text-end">
                                <div className="text-muted small">Валюта: {invoice.currency}</div>
                                <div className="fs-5"><strong>Итого: {total.toFixed(2)} {invoice.currency}</strong></div>
                            </div>
                        </div>
                        <Table hover responsive size="sm">
                            <thead className="table-light">
                                <tr>
                                    <th>Дата запуска анализа</th>
                                    <th>Дата текущего статуса</th>
                                    <th>Номер отчёта</th>
                                    <th>Состояние</th>
                                    <th style={{ width: 160 }}>Цена</th>
                                </tr>
                            </thead>
                            <tbody>
                                {groups.map((g) => (
                                    <React.Fragment key={g.doctorId ?? 'unknown'}>
                                        <tr className="table-secondary">
                                            <td colSpan={4} className="fw-semibold">
                                                Врач: {g.doctorName} <span className="text-muted fw-normal">({g.items.length})</span>
                                            </td>
                                            <td className="text-end text-muted small">
                                                {subtotalFor(g.items).toFixed(2)} {invoice.currency}
                                            </td>
                                        </tr>
                                        {g.items.map((it) => (
                                            <tr key={it.id}>
                                                <td>{it.startedAt?.replace('T', ' ').slice(0, 16) ?? '—'}</td>
                                                <td>{it.terminalStateAt?.replace('T', ' ').slice(0, 16) ?? '—'}</td>
                                                <td><code>{it.reportNumber ?? '—'}</code></td>
                                                <td><Badge bg={stateVariants[it.billingState]}>{stateLabels[it.billingState]}</Badge></td>
                                                <td>
                                                    <Form.Control type="number" min="0" step="100" size="sm"
                                                                  value={prices[it.id] ?? ''}
                                                                  onChange={(e) => setPrices({ ...prices, [it.id]: e.target.value })} />
                                                </td>
                                            </tr>
                                        ))}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </Table>
                    </>
                ) : null}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-secondary" onClick={onClose} disabled={saving}>Отмена</Button>
                <Button variant="primary" onClick={save} disabled={saving || !invoice}>
                    {saving ? <Spinner size="sm" animation="border" /> : 'Сохранить цены'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default EditInvoicePricesModal;
